/**
 * The document shape both directions of the meeting-note converter read and
 * write, plus the constants that have to agree with the editor schema.
 *
 * The schema itself belongs to `@kiao-voice/ui-kit/meeting-notes-extensions`
 * and is NOT modelled here — see `../blog/tiptap-document.ts` for why a second
 * copy of a Tiptap schema is a liability. What this module holds is only the
 * part BOTH directions have to agree on, and every value in it is pinned
 * against the real ProseMirror schema by
 * `packages/ui-kit/src/meeting-notes-markdown-doc.test.ts`: a default that
 * drifts from the schema produces a document that is `toJSON`-unequal to the
 * one the editor stores for the same note, which is exactly the divergence this
 * package cannot detect on its own.
 */

/** One mark on a text run. The note schema's marks carry no attributes. */
export type MeetingNotesMark = {
  type: string
}

/** One node of a Tiptap document, in the exact shape `Node.toJSON()` produces. */
export type MeetingNotesNode = {
  type: string
  attrs?: Record<string, unknown>
  content?: MeetingNotesNode[]
  text?: string
  marks?: MeetingNotesMark[]
}

/**
 * A whole note. Kept as a type alias rather than an interface so it stays
 * assignable to the index-signature types Tiptap's own helpers declare.
 */
export type MeetingNotesDoc = {
  type: 'doc'
  content: MeetingNotesNode[]
}

/**
 * The heading levels the schema carries. `h1` is the page's own title; `h4` and
 * below have no node, so Markdown that asks for one degrades to a paragraph.
 */
export const MEETING_NOTES_HEADING_LEVELS = [2, 3] as const

/**
 * Marks in the order ProseMirror keeps them in — `Mark.addToSet` inserts by the
 * mark type's rank, which is its position in `schema.marks`. A parser that
 * emitted them in authoring order would build a document the editor would never
 * store for the same note.
 */
export const MEETING_NOTES_MARK_ORDER = ['bold', 'italic'] as const

/**
 * Attributes a table cell carries. `colspan`/`rowspan` exist because
 * prosemirror-tables reads them by name; `colwidth`/`align` are neutralized in
 * the schema. Only the all-default shape is expressible in Markdown.
 */
export const MEETING_NOTES_TABLE_CELL_ATTRS = {
  colspan: 1,
  rowspan: 1,
  colwidth: null,
  align: null,
} as const

/**
 * The ordered list's attributes. Both are neutralized in the schema (a note has
 * no「從 2026 開始編號」), but they EXIST there, so `toJSON` writes them out and a
 * document that omits them is unequal to the editor's.
 */
export const MEETING_NOTES_ORDERED_LIST_ATTRS = {
  start: null,
  type: null,
} as const
